import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import SerenityProductCard from '../components/SerenityProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Filter, X, SlidersHorizontal, Grid, List, Loader2, ChevronDown, ChevronUp, Search, Check } from 'lucide-react';
import { productsAPI, categoriesAPI, brandsAPI } from '../services/api';

const SuperShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug } = useParams(); // Get category/brand slug from URL path
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false); // Start closed on mobile, sidebar always visible on desktop
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Data from API
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Pagination - classic page numbers
  const productsPerPage = 24;
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters - initialized from URL in useEffect
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [minPrice, setMinPrice] = useState('0');
  const [maxPrice, setMaxPrice] = useState('200');
  const [minRating, setMinRating] = useState(0);
  const [showNewOnly, setShowNewOnly] = useState(false);
  const [showSaleOnly, setShowSaleOnly] = useState(false);
  const [showBestSellers, setShowBestSellers] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [pageTitle, setPageTitle] = useState('All Products');
  
  // Track if initial load from slug/URL is complete
  const [slugProcessed, setSlugProcessed] = useState(false);
  const [urlInitialized, setUrlInitialized] = useState(false);
  
  // Accordion states
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    brands: true,
    price: true,
    rating: false,
    quickFilters: true
  });
  
  // Search within filters
  const [categorySearch, setCategorySearch] = useState('');
  const [brandSearch, setBrandSearch] = useState('');
  
  // Dynamic filter counts
  const [dynamicCounts, setDynamicCounts] = useState({ category_counts: {}, brand_counts: {} });
  
  // Pending filters (not applied yet)
  const [pendingPriceRange, setPendingPriceRange] = useState([0, 200]);
  const [pendingMinPrice, setPendingMinPrice] = useState('0');
  const [pendingMaxPrice, setPendingMaxPrice] = useState('200');
  const [filtersChanged, setFiltersChanged] = useState(false);

  // Toggle accordion section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper to normalize strings for comparison
  const normalizeForMatch = (str) => {
    return str.toLowerCase()
      .replace(/&/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Sync filters to URL - called when filters change
  const updateURL = (newFilters) => {
    const params = new URLSearchParams();
    
    // Preserve search param if exists
    const searchParam = searchParams.get('search');
    if (searchParam) params.set('search', searchParam);
    
    // Categories
    if (newFilters.categories && newFilters.categories.length > 0) {
      params.set('categories', newFilters.categories.join(','));
    }
    
    // Brands
    if (newFilters.brands && newFilters.brands.length > 0) {
      params.set('brands', newFilters.brands.join(','));
    }
    
    // Price range (only if not default)
    if (newFilters.minPrice !== undefined && newFilters.minPrice > 0) {
      params.set('min_price', newFilters.minPrice.toString());
    }
    if (newFilters.maxPrice !== undefined && newFilters.maxPrice < 200) {
      params.set('max_price', newFilters.maxPrice.toString());
    }
    
    // Quick filters
    if (newFilters.isNew) params.set('new', 'true');
    if (newFilters.onSale) params.set('sale', 'true');
    if (newFilters.bestSellers) params.set('best_sellers', 'true');
    if (newFilters.inStock) params.set('in_stock', 'true');
    
    // Rating
    if (newFilters.rating && newFilters.rating > 0) {
      params.set('rating', newFilters.rating.toString());
    }
    
    // Sort (only if not default)
    if (newFilters.sort && newFilters.sort !== 'featured') {
      params.set('sort', newFilters.sort);
    }
    
    // Page number (only if not page 1)
    if (newFilters.page && newFilters.page > 1) {
      params.set('page', newFilters.page.toString());
    }
    
    // Update URL without navigation (replace current history entry)
    setSearchParams(params, { replace: true });
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalProducts / productsPerPage);

  // Initial load: fetch categories, brands, and initial counts
  useEffect(() => {
    let isMounted = true;
    
    const fetchInitialData = async () => {
      try {
        const [categoriesRes, brandsRes, filterCountsRes] = await Promise.all([
          categoriesAPI.getAll(),
          brandsAPI.getAll(),
          productsAPI.getDynamicFilterCounts()
        ]);
        
        if (isMounted) {
          const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : 
                                Array.isArray(categoriesRes) ? categoriesRes : [];
          const brandsData = Array.isArray(brandsRes.data) ? brandsRes.data : 
                            Array.isArray(brandsRes) ? brandsRes : [];
          const filterCounts = filterCountsRes.data || filterCountsRes || { category_counts: {}, brand_counts: {} };
          
          setCategories(categoriesData);
          setBrands(brandsData);
          setDynamicCounts(filterCounts);
          
          console.log(`✅ Initial data loaded: ${categoriesData.length} categories, ${brandsData.length} brands`);
          
          let initialCategories = [];
          let initialBrands = [];
          let initialTitle = 'All Products';
          let initialNewOnly = false;
          let initialSaleOnly = false;
          
          // Handle slug from URL path (e.g., /shop/category/toys)
          if (slug) {
            const slugLower = slug.toLowerCase();
            
            // Try to match category
            const matchedCategory = categoriesData.find(cat => {
              const catName = (cat.name || cat);
              const catSlug = (cat.slug || catName);
              if (catSlug.toLowerCase() === slugLower) return true;
              if (normalizeForMatch(catSlug) === slugLower) return true;
              if (normalizeForMatch(catName) === slugLower) return true;
              return false;
            });
            
            if (matchedCategory) {
              const categoryName = matchedCategory.name || matchedCategory;
              initialCategories = [categoryName];
              initialTitle = categoryName;
              console.log(`✅ Matched category from slug "${slug}": ${categoryName}`);
            } else {
              // Try to match brand
              const matchedBrand = brandsData.find(brand => {
                const brandName = (brand.name || brand);
                const brandSlug = (brand.slug || brandName);
                if (brandSlug.toLowerCase() === slugLower) return true;
                if (normalizeForMatch(brandSlug) === slugLower) return true;
                if (normalizeForMatch(brandName) === slugLower) return true;
                return false;
              });
              
              if (matchedBrand) {
                const brandName = matchedBrand.name || matchedBrand;
                initialBrands = [brandName];
                initialTitle = brandName;
                console.log(`✅ Matched brand from slug "${slug}": ${brandName}`);
              }
            }
          }
          
          // === READ ALL FILTER PARAMS FROM URL (only if not using slug route) ===
          if (!slug) {
            // Categories - support both single 'category' and multiple 'categories'
            const categoriesParam = searchParams.get('categories');
            const categoryParam = searchParams.get('category');
            if (categoriesParam) {
              initialCategories = categoriesParam.split(',').filter(c => c.trim());
              initialTitle = initialCategories.length === 1 ? initialCategories[0] : 'Filtered Products';
            } else if (categoryParam) {
              initialCategories = [categoryParam];
              initialTitle = categoryParam;
            }
          
            // Brands - support both single 'brand' and multiple 'brands'
            const brandsParam = searchParams.get('brands');
            const brandParam = searchParams.get('brand');
            if (brandsParam) {
              initialBrands = brandsParam.split(',').filter(b => b.trim());
              if (initialCategories.length === 0) {
                initialTitle = initialBrands.length === 1 ? initialBrands[0] : 'Filtered Products';
              }
            } else if (brandParam) {
              initialBrands = [brandParam];
              if (initialCategories.length === 0) {
                initialTitle = brandParam;
              }
            }
          
            // Price range
            const minPriceParam = searchParams.get('min_price');
            const maxPriceParam = searchParams.get('max_price');
            let initialMinPrice = 0;
            let initialMaxPrice = 200;
            if (minPriceParam) initialMinPrice = parseInt(minPriceParam) || 0;
            if (maxPriceParam) initialMaxPrice = parseInt(maxPriceParam) || 200;
          
            // Quick filters
            const newParam = searchParams.get('new');
            const saleParam = searchParams.get('sale');
            const bestSellersParam = searchParams.get('best_sellers');
            const inStockParam = searchParams.get('in_stock');
            if (newParam === 'true') initialNewOnly = true;
            if (saleParam === 'true') initialSaleOnly = true;
            let initialBestSellers = bestSellersParam === 'true';
            let initialInStock = inStockParam === 'true';
          
            // Rating
            const ratingParam = searchParams.get('rating');
            let initialRating = 0;
            if (ratingParam) initialRating = parseInt(ratingParam) || 0;
          
            // Sort
            const sortParam = searchParams.get('sort');
            let initialSort = 'featured';
            if (sortParam) initialSort = sortParam;
            
            // Page number
            const pageParam = searchParams.get('page');
            let initialPage = 1;
            if (pageParam) initialPage = parseInt(pageParam) || 1;
          
            // Collection param (legacy support)
            const collectionParam = searchParams.get('collection');
            if (collectionParam) {
              switch (collectionParam) {
                case 'new-arrivals':
                  initialNewOnly = true;
                  initialTitle = 'New Arrivals';
                  break;
                case 'sale':
                  initialSaleOnly = true;
                  initialTitle = 'On Sale';
                  break;
                case 'best-sellers':
                  initialBestSellers = true;
                  initialTitle = 'Best Sellers';
                  break;
                case 'gifts':
                  initialTitle = 'Gift Ideas';
                  break;
                default:
                  break;
              }
            }
          
            // Search param
            const searchParam = searchParams.get('search');
            if (searchParam) {
              initialTitle = `Search: "${searchParam}"`;
            }
          
            // Update title for combined filters
            if (initialCategories.length > 0 || initialBrands.length > 0) {
              if (initialCategories.length + initialBrands.length > 1) {
                initialTitle = 'Filtered Products';
              }
            }
          
            // Set all initial filters from URL
            setPriceRange([initialMinPrice, initialMaxPrice]);
            setPendingPriceRange([initialMinPrice, initialMaxPrice]);
            setPendingMinPrice(initialMinPrice.toString());
            setPendingMaxPrice(initialMaxPrice.toString());
            setInStockOnly(initialInStock);
            setShowBestSellers(initialBestSellers);
            setMinRating(initialRating);
            setSortBy(initialSort);
            setCurrentPage(initialPage);
          } // end if (!slug)
          
          // Set common initial filters
          setSelectedCategories(initialCategories);
          setSelectedBrands(initialBrands);
          setShowNewOnly(initialNewOnly);
          setShowSaleOnly(initialSaleOnly);
          setPageTitle(initialTitle);
          setUrlInitialized(true);
          setSlugProcessed(true);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError' && !error.cancelled) {
          console.error('Error fetching initial data:', error);
        }
        if (isMounted) {
          setUrlInitialized(true);
          setSlugProcessed(true);
        }
      }
    };
    
    fetchInitialData();
    
    return () => {
      isMounted = false;
    };
  }, [slug]); // Remove searchParams from deps to avoid re-init on URL changes

  // Sync filters to URL when they change (after initial URL load)
  useEffect(() => {
    if (!urlInitialized || !slugProcessed) return;
    
    // Don't update URL if we're on a slug route (e.g., /shop/category/toys)
    if (slug) return;
    
    updateURL({
      categories: selectedCategories,
      brands: selectedBrands,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
      isNew: showNewOnly,
      onSale: showSaleOnly,
      bestSellers: showBestSellers,
      inStock: inStockOnly,
      rating: minRating,
      sort: sortBy,
      page: currentPage
    });
  }, [urlInitialized, slugProcessed, slug, selectedCategories, selectedBrands, priceRange, showNewOnly, showSaleOnly, showBestSellers, inStockOnly, minRating, sortBy, currentPage]);

  // Reset to page 1 when filters change (but not on initial load)
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  useEffect(() => {
    if (slugProcessed && urlInitialized) {
      if (filtersInitialized) {
        // Only reset page when user changes filters after initial load
        setCurrentPage(1);
      } else {
        // Mark filters as initialized after first render
        setFiltersInitialized(true);
      }
    }
  }, [selectedCategories, selectedBrands, priceRange, showNewOnly, showSaleOnly, showBestSellers, inStockOnly, sortBy]);

  // Fetch products when filters or page change (after initial slug processing)
  useEffect(() => {
    if (!slugProcessed) return;
    
    let isMounted = true;
    
    const loadProducts = async () => {
      setLoading(true);
      
      try {
        // Build params directly from state - use skip for page-based pagination
        const skip = (currentPage - 1) * productsPerPage;
        const params = { limit: productsPerPage, skip: skip };
        
        // Use 'categories' param for multiple selections
        if (selectedCategories.length > 0) {
          params.categories = selectedCategories.join(',');
        }
        // Use 'brands' param for multiple selections
        if (selectedBrands.length > 0) {
          params.brands = selectedBrands.join(',');
        }
        if (priceRange[0] > 0) params.min_price = priceRange[0];
        if (priceRange[1] < 200) params.max_price = priceRange[1];
        if (showNewOnly) params.is_new = true;
        if (showSaleOnly) params.on_sale = true;
        if (showBestSellers) params.is_best_seller = true;
        if (inStockOnly) params.in_stock = true;
        
        const searchParam = searchParams.get('search');
        if (searchParam) params.search = searchParam;
        
        // Fetch products and count in parallel
        const [productsRes, countRes] = await Promise.all([
          productsAPI.getAll(params),
          productsAPI.getCount(params)
        ]);
        
        if (!isMounted) return;
        
        const productsData = Array.isArray(productsRes.data) ? productsRes.data : 
                            Array.isArray(productsRes) ? productsRes : [];
        const total = countRes.data?.count ?? countRes.count ?? productsData.length;
        
        setProducts(productsData);
        setTotalProducts(total);
        
        console.log(`✅ Page ${currentPage}: ${productsData.length} products (total: ${total})`);
        
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
      } catch (error) {
        if (error.name !== 'CanceledError' && !error.cancelled) {
          console.error('Error fetching products:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadProducts();
    
    return () => {
      isMounted = false;
    };
  }, [slugProcessed, selectedCategories, selectedBrands, priceRange, showNewOnly, showSaleOnly, showBestSellers, inStockOnly, sortBy, searchParams, currentPage]);

  // Sync price inputs with slider
  useEffect(() => {
    setMinPrice(String(priceRange[0]));
    setMaxPrice(String(priceRange[1]));
  }, [priceRange]);

  // Handle price input changes
  const handlePriceInputChange = (type, value) => {
    const numValue = parseInt(value) || 0;
    if (type === 'min') {
      setMinPrice(value);
      if (!isNaN(numValue)) {
        setPriceRange([Math.min(numValue, priceRange[1]), priceRange[1]]);
      }
    } else {
      setMaxPrice(value);
      if (!isNaN(numValue)) {
        setPriceRange([priceRange[0], Math.max(numValue, priceRange[0])]);
      }
    }
  };

  // Normalize product data
  const normalizeProduct = (product) => ({
    ...product,
    id: product._id || product.id,
    salePrice: product.sale_price || product.salePrice,
    inStock: product.in_stock !== undefined ? product.in_stock : (product.inStock !== undefined ? product.inStock : true),
    isNew: product.is_new || product.isNew,
    isBestSeller: product.is_best_seller || product.isBestSeller,
    reviewCount: product.review_count || product.reviewCount || 0,
    brand: product.brand || 'BOXVERRA',
  });

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
    // Update page title
    setPageTitle(prev => {
      const newSelection = selectedCategories.includes(cat) 
        ? selectedCategories.filter(c => c !== cat)
        : [...selectedCategories, cat];
      if (newSelection.length === 0 && selectedBrands.length === 0) return 'All Products';
      if (newSelection.length === 1 && selectedBrands.length === 0) return newSelection[0];
      return 'Filtered Products';
    });
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
    // Update page title
    setPageTitle(prev => {
      const newSelection = selectedBrands.includes(brand)
        ? selectedBrands.filter(b => b !== brand)
        : [...selectedBrands, brand];
      if (newSelection.length === 0 && selectedCategories.length === 0) return 'All Products';
      if (newSelection.length === 1 && selectedCategories.length === 0) return newSelection[0];
      return 'Filtered Products';
    });
  };

  // Remove single filter
  const removeFilter = (type, value) => {
    switch (type) {
      case 'category':
        setSelectedCategories(prev => prev.filter(c => c !== value));
        break;
      case 'brand':
        setSelectedBrands(prev => prev.filter(b => b !== value));
        break;
      case 'new':
        setShowNewOnly(false);
        break;
      case 'bestSellers':
        setShowBestSellers(false);
        break;
      case 'sale':
        setShowSaleOnly(false);
        break;
      case 'stock':
        setInStockOnly(false);
        break;
      case 'rating':
        setMinRating(0);
        break;
      default:
        break;
    }
  };

  // Filter categories/brands by search
  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );
  const filteredBrands = brands.filter(brand => 
    brand.name.toLowerCase().includes(brandSearch.toLowerCase())
  );

  // Products are already filtered server-side, just normalize them
  let displayProducts = products.map(normalizeProduct);
  
  // Apply rating filter (not supported by API)
  if (minRating > 0) {
    displayProducts = displayProducts.filter(p => (p.rating || 0) >= minRating);
  }
  
  // Apply collection filter for best-sellers (client-side)
  const collectionParam = searchParams.get('collection');
  if (collectionParam === 'best-sellers') {
    displayProducts = displayProducts.filter(p => p.isBestSeller);
  }

  // Apply sorting (client-side for now)
  const sortedProducts = [...displayProducts].sort((a, b) => {
    const priceA = a.salePrice || a.price;
    const priceB = b.salePrice || b.price;
    
    switch (sortBy) {
      case 'price-low': return priceA - priceB;
      case 'price-high': return priceB - priceA;
      case 'newest': return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
      case 'best-selling': return (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0);
      case 'top-rated': return b.rating - a.rating;
      case 'name-az': return a.name.localeCompare(b.name);
      default: return 0;
    }
  });

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (totalPages <= maxVisiblePages + 2) {
        // Show all pages if not too many
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        if (currentPage > 3) {
          pages.push('...');
        }
        
        // Show pages around current page
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        
        for (let i = start; i <= end; i++) {
          if (!pages.includes(i)) pages.push(i);
        }
        
        if (currentPage < totalPages - 2) {
          pages.push('...');
        }
        
        // Always show last page
        if (!pages.includes(totalPages)) {
          pages.push(totalPages);
        }
      }
      
      return pages;
    };
    
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages && page !== currentPage) {
        setCurrentPage(page);
      }
    };
    
    return (
      <div className="flex items-center justify-center gap-2 mt-10">
        {/* Previous button */}
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-lg font-body transition-colors ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white border border-[#E8E4DE] text-text-heading hover:border-[#D4A574] hover:text-[#D4A574]'
          }`}
        >
          ← Prev
        </button>
        
        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-3 py-2 text-text-muted">...</span>
            ) : (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`min-w-[40px] h-10 rounded-lg font-body transition-colors ${
                  currentPage === page
                    ? 'bg-[#D4A574] text-white font-semibold'
                    : 'bg-white border border-[#E8E4DE] text-text-heading hover:border-[#D4A574] hover:text-[#D4A574]'
                }`}
              >
                {page}
              </button>
            )
          ))}
        </div>
        
        {/* Next button */}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-lg font-body transition-colors ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white border border-[#E8E4DE] text-text-heading hover:border-[#D4A574] hover:text-[#D4A574]'
          }`}
        >
          Next →
        </button>
      </div>
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setPriceRange([0, 200]);
    setPendingPriceRange([0, 200]);
    setPendingMinPrice('0');
    setPendingMaxPrice('200');
    setFiltersChanged(false);
    setMinRating(0);
    setShowNewOnly(false);
    setShowSaleOnly(false);
    setShowBestSellers(false);
    setInStockOnly(false);
    setSortBy('featured');
    setCurrentPage(1);
    setPageTitle('All Products');
    
    // Clear URL params (keep only search if present)
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchParams({ search: searchParam }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Collect active filters for display
  const activeFilters = [
    ...selectedCategories.map(c => ({ type: 'category', value: c, label: c })),
    ...selectedBrands.map(b => ({ type: 'brand', value: b, label: b })),
    ...(showNewOnly ? [{ type: 'new', value: 'new', label: 'New Arrivals' }] : []),
    ...(showBestSellers ? [{ type: 'bestSellers', value: 'bestSellers', label: 'Best Sellers' }] : []),
    ...(showSaleOnly ? [{ type: 'sale', value: 'sale', label: 'On Sale' }] : []),
    ...(inStockOnly ? [{ type: 'stock', value: 'stock', label: 'In Stock' }] : []),
  ];

  // Fetch dynamic filter counts when filters change
  useEffect(() => {
    if (!slugProcessed) return;
    
    let isMounted = true;
    
    const fetchDynamicCounts = async () => {
      try {
        const params = {};
        if (selectedCategories.length === 1) params.category = selectedCategories[0];
        if (selectedBrands.length === 1) params.brand = selectedBrands[0];
        if (priceRange[0] > 0) params.min_price = priceRange[0];
        if (priceRange[1] < 200) params.max_price = priceRange[1];
        if (showNewOnly) params.is_new = true;
        if (showSaleOnly) params.on_sale = true;
        if (showBestSellers) params.is_best_seller = true;
        if (inStockOnly) params.in_stock = true;
        
        const searchParam = searchParams.get('search');
        if (searchParam) params.search = searchParam;
        
        const response = await productsAPI.getDynamicFilterCounts(params);
        if (isMounted) {
          setDynamicCounts(response.data || response || { category_counts: {}, brand_counts: {} });
        }
      } catch (error) {
        console.error('Error fetching dynamic counts:', error);
      }
    };
    
    fetchDynamicCounts();
    
    return () => {
      isMounted = false;
    };
  }, [slugProcessed, selectedCategories, selectedBrands, priceRange, showNewOnly, showSaleOnly, inStockOnly, searchParams]);

  // Calculate product counts for filters (from server-side counts)
  const getCountForCategory = (categoryName) => {
    return dynamicCounts.category_counts?.[categoryName] || 0;
  };
  
  const getCountForBrand = (brandName) => {
    return dynamicCounts.brand_counts?.[brandName] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  // Custom Checkbox Component
  const CustomCheckbox = ({ checked, onChange, children }) => (
    <label 
      className="flex items-center gap-3 cursor-pointer group"
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
    >
      <div 
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
          checked 
            ? 'bg-[#D4A574] border-[#D4A574]' 
            : 'border-[#E8E4DE] group-hover:border-[#D4A574]'
        }`}
      >
        {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </div>
      {children}
    </label>
  );

  // Custom Radio Component
  const CustomRadio = ({ checked, onChange, children }) => (
    <label 
      className="flex items-center gap-3 cursor-pointer group"
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
    >
      <div 
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
          checked 
            ? 'border-[#D4A574]' 
            : 'border-[#E8E4DE] group-hover:border-[#D4A574]'
        }`}
      >
        {checked && <div className="w-2.5 h-2.5 rounded-full bg-[#D4A574]" />}
      </div>
      {children}
    </label>
  );

  return (
    <div className="min-h-screen bg-bg-main">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-text-heading mb-3 font-heading">{pageTitle}</h1>
          <p className="text-lg text-text-body font-body">
            {totalProducts} {totalProducts === 1 ? 'product' : 'products'} found
          </p>
        </div>

        {/* Active Filters Tags */}
        {activeFilters.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="text-sm text-text-muted font-body mr-2">Active filters:</span>
            {activeFilters.map((filter, idx) => (
              <button
                key={`${filter.type}-${filter.value}-${idx}`}
                onClick={() => removeFilter(filter.type, filter.value)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#D4A574]/10 text-[#D4A574] rounded-full text-sm font-medium font-body hover:bg-[#D4A574]/20 transition-colors"
              >
                {filter.label}
                <X className="h-3.5 w-3.5" />
              </button>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-sm text-text-muted hover:text-[#D4A574] font-body ml-2 underline"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Button - Fixed at bottom - HIGHER Z-INDEX */}
          <div className="lg:hidden fixed bottom-6 left-4 right-4 z-[100]">
            <Button
              onClick={() => setShowFilters(true)}
              className="w-full bg-[#2C2824] hover:bg-[#1a1816] text-white py-5 rounded-2xl shadow-2xl flex items-center justify-center gap-3 text-lg font-semibold"
            >
              <Filter className="h-6 w-6" />
              Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
            </Button>
          </div>

          {/* FILTERS SIDEBAR - Desktop always visible, Mobile in drawer */}
          <aside className={`
            ${showFilters ? 'fixed inset-0 z-50' : 'hidden'} 
            lg:relative lg:block lg:z-auto lg:w-80 flex-shrink-0
          `}>
            {/* Mobile Overlay */}
            {showFilters && <div className="lg:hidden fixed inset-0 bg-black/50" onClick={() => setShowFilters(false)} />}
            
            <div className={`
              ${showFilters ? 'fixed right-0 top-0 h-full w-[85%] max-w-sm z-50' : 'hidden'} 
              lg:relative lg:block lg:w-80 lg:h-auto lg:right-auto lg:top-auto
              bg-white lg:rounded-2xl border-l lg:border border-[#E8E4DE] shadow-xl lg:shadow-sm lg:sticky lg:top-4 overflow-hidden
            `}>
              <div className="flex items-center justify-between p-5 border-b border-[#E8E4DE] bg-white">
                <h3 className="text-xl font-bold text-text-heading font-heading flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5 text-[#D4A574]" />
                  Filters
                </h3>
                <button onClick={() => setShowFilters(false)} className="lg:hidden p-2 hover:bg-[#F7F4EF] rounded-lg">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* CATEGORIES - Accordion */}
                <div className="border-b border-[#E8E4DE]">
                  <button 
                    onClick={() => toggleSection('categories')}
                    className="w-full flex items-center justify-between p-5 hover:bg-[#F7F4EF] transition-colors"
                  >
                    <h4 className="text-base font-bold text-text-heading font-heading">Categories</h4>
                    {expandedSections.categories ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>
                  {expandedSections.categories && (
                    <div className="px-5 pb-5">
                      {/* Search within categories */}
                      {categories.length > 5 && (
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                          <Input
                            type="text"
                            placeholder="Search categories..."
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            className="pl-9 py-2 text-sm border-[#E8E4DE] focus:border-[#D4A574]"
                          />
                        </div>
                      )}
                      <div className="space-y-2.5 max-h-48 overflow-y-auto">
                        {filteredCategories.map((cat) => {
                          const count = getCountForCategory(cat.name);
                          return (
                            <CustomCheckbox
                              key={cat._id || cat.id}
                              checked={selectedCategories.includes(cat.name)}
                              onChange={() => toggleCategory(cat.name)}
                            >
                              <span className="flex-1 text-sm text-text-body font-body group-hover:text-[#D4A574] transition-colors">
                                {cat.name}
                              </span>
                              <span className={`text-xs font-body ${count === 0 ? 'text-red-400' : 'text-text-muted'}`}>
                                ({count})
                              </span>
                            </CustomCheckbox>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* BRANDS - Accordion */}
                <div className="border-b border-[#E8E4DE]">
                  <button 
                    onClick={() => toggleSection('brands')}
                    className="w-full flex items-center justify-between p-5 hover:bg-[#F7F4EF] transition-colors"
                  >
                    <h4 className="text-base font-bold text-text-heading font-heading">Brands</h4>
                    {expandedSections.brands ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>
                  {expandedSections.brands && (
                    <div className="px-5 pb-5">
                      {/* Search within brands */}
                      {brands.length > 5 && (
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                          <Input
                            type="text"
                            placeholder="Search brands..."
                            value={brandSearch}
                            onChange={(e) => setBrandSearch(e.target.value)}
                            className="pl-9 py-2 text-sm border-[#E8E4DE] focus:border-[#D4A574]"
                          />
                        </div>
                      )}
                      <div className="space-y-2.5 max-h-48 overflow-y-auto">
                        {filteredBrands.map((brand) => {
                          const count = getCountForBrand(brand.name);
                          return (
                            <CustomCheckbox
                              key={brand._id || brand.id}
                              checked={selectedBrands.includes(brand.name)}
                              onChange={() => toggleBrand(brand.name)}
                            >
                            <span className="flex-1 text-sm text-text-body font-body group-hover:text-[#D4A574] transition-colors">
                              {brand.name}
                            </span>
                            <span className={`text-xs font-body ${count === 0 ? 'text-red-400' : 'text-text-muted'}`}>
                              ({count})
                            </span>
                            </CustomCheckbox>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* PRICE RANGE - Accordion */}
                <div className="border-b border-[#E8E4DE]">
                  <button 
                    onClick={() => toggleSection('price')}
                    className="w-full flex items-center justify-between p-5 hover:bg-[#F7F4EF] transition-colors"
                  >
                    <h4 className="text-base font-bold text-text-heading font-heading">Price Range</h4>
                    {expandedSections.price ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>
                  {expandedSections.price && (
                    <div className="px-5 pb-5">
                      <Slider
                        value={pendingPriceRange}
                        onValueChange={(value) => {
                          setPendingPriceRange(value);
                          setPendingMinPrice(value[0].toString());
                          setPendingMaxPrice(value[1].toString());
                          setFiltersChanged(true);
                        }}
                        min={0}
                        max={200}
                        step={5}
                        className="mb-4"
                      />
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-xs text-text-muted mb-1 block font-body">Min ($)</label>
                          <Input
                            type="number"
                            value={pendingMinPrice}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPendingMinPrice(val);
                              const numVal = parseInt(val) || 0;
                              if (numVal >= 0 && numVal <= pendingPriceRange[1]) {
                                setPendingPriceRange([numVal, pendingPriceRange[1]]);
                              }
                              setFiltersChanged(true);
                            }}
                            className="text-sm border-[#E8E4DE] focus:border-[#D4A574]"
                            min={0}
                            max={200}
                          />
                        </div>
                        <span className="text-text-muted mt-5">—</span>
                        <div className="flex-1">
                          <label className="text-xs text-text-muted mb-1 block font-body">Max ($)</label>
                          <Input
                            type="number"
                            value={pendingMaxPrice}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPendingMaxPrice(val);
                              const numVal = parseInt(val) || 200;
                              if (numVal >= pendingPriceRange[0] && numVal <= 200) {
                                setPendingPriceRange([pendingPriceRange[0], numVal]);
                              }
                              setFiltersChanged(true);
                            }}
                            className="text-sm border-[#E8E4DE] focus:border-[#D4A574]"
                            min={0}
                            max={200}
                          />
                        </div>
                      </div>
                      
                      {/* Apply Filters Button */}
                      {filtersChanged && (
                        <Button
                          onClick={() => {
                            setPriceRange(pendingPriceRange);
                            setMinPrice(pendingMinPrice);
                            setMaxPrice(pendingMaxPrice);
                            setFiltersChanged(false);
                          }}
                          className="w-full mt-4 bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] font-bold font-body"
                        >
                          Apply Price Filter
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* QUICK FILTERS - Accordion */}
                <div className="border-b border-[#E8E4DE]">
                  <button 
                    onClick={() => toggleSection('quickFilters')}
                    className="w-full flex items-center justify-between p-5 hover:bg-[#F7F4EF] transition-colors"
                  >
                    <h4 className="text-base font-bold text-text-heading font-heading">Quick Filters</h4>
                    {expandedSections.quickFilters ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>
                  {expandedSections.quickFilters && (
                    <div className="px-5 pb-5 space-y-2.5">
                      <CustomCheckbox checked={showNewOnly} onChange={() => setShowNewOnly(!showNewOnly)}>
                        <span className="text-sm text-text-body font-body group-hover:text-[#D4A574] transition-colors">
                          New Arrivals
                        </span>
                      </CustomCheckbox>
                      <CustomCheckbox checked={showBestSellers} onChange={() => setShowBestSellers(!showBestSellers)}>
                        <span className="text-sm text-text-body font-body group-hover:text-[#D4A574] transition-colors">
                          Best Sellers
                        </span>
                      </CustomCheckbox>
                    </div>
                  )}
                </div>
              </div>

              {/* CLEAR FILTERS */}
              {activeFilters.length > 0 && (
                <div className="p-5 border-t border-[#E8E4DE]">
                  <Button
                    onClick={clearAllFilters}
                    variant="outline"
                    className="w-full border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574] hover:text-white font-body"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}

              {/* Mobile Apply Button */}
              <div className="lg:hidden p-5 border-t border-[#E8E4DE] bg-white sticky bottom-0">
                <Button
                  onClick={() => setShowFilters(false)}
                  className="w-full bg-[#D4A574] hover:bg-[#C69963] text-white py-4 font-body font-semibold"
                >
                  Show {sortedProducts.length} Results
                </Button>
              </div>
            </div>
          </aside>

          {/* PRODUCTS AREA */}
          <div className="flex-1 pb-20 lg:pb-0">
            {/* TOOLBAR */}
            <div className="bg-white rounded-xl p-4 mb-6 border border-[#E8E4DE] shadow-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {/* Desktop Filter Toggle - hidden on mobile since we use drawer */}
                  <Button
                    onClick={() => setShowFilters(!showFilters)}
                    className="hidden lg:flex bg-[#D4A574] hover:bg-[#C69963] text-white font-body"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
                  </Button>

                  {/* View Mode Toggle */}
                  <div className="hidden sm:flex gap-1 border border-[#E8E4DE] rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'grid' ? 'bg-[#D4A574] text-white' : 'text-text-muted hover:text-[#D4A574]'
                      }`}
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'list' ? 'bg-[#D4A574] text-white' : 'text-text-muted hover:text-[#D4A574]'
                      }`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* SORT */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-sm text-text-body font-body whitespace-nowrap">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 sm:flex-none px-4 py-2 border border-[#E8E4DE] rounded-lg bg-white text-text-heading font-body text-sm focus:border-[#D4A574] focus:ring-1 focus:ring-[#D4A574] outline-none"
                  >
                    <option value="featured">Featured</option>
                    <option value="newest">Newest First</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="best-selling">Best Selling</option>
                    <option value="top-rated">Top Rated</option>
                    <option value="name-az">Name: A-Z</option>
                  </select>
                </div>
              </div>
            </div>

            {/* PRODUCTS GRID */}
            {sortedProducts.length > 0 ? (
              <>
                {/* Results count */}
                <div className="mb-4 text-sm text-text-muted font-body">
                  Showing {((currentPage - 1) * productsPerPage) + 1}-{Math.min(currentPage * productsPerPage, totalProducts)} of {totalProducts} products
                </div>
                
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' 
                  : 'space-y-6'
                }>
                  {sortedProducts.map((product) => (
                    <SerenityProductCard key={product.id || product._id} product={product} />
                  ))}
                </div>
                
                {/* Pagination */}
                <Pagination />
              </>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-[#E8E4DE]">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-2xl font-bold text-text-heading mb-2 font-heading">No products found</h3>
                <p className="text-text-body font-body mb-6">Try adjusting your filters or search criteria</p>
                <Button 
                  onClick={clearAllFilters}
                  className="bg-[#D4A574] hover:bg-[#C69963] text-white font-body"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperShopPage;
